import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { QRCodeSVG } from 'qrcode.react';
import { ArrowLeft, Printer, Car, Smartphone, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const FRONTEND_URL = window.location.origin;

export default function LoanCarQRPrint() {
  const navigate = useNavigate();
  const printRef = useRef(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: 'include'
        });
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        } else {
          navigate('/login');
        }
      } catch (error) {
        console.error('Failed to fetch user:', error);
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [navigate]);

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent"></div>
      </div>
    );
  }

  // Generate permanent kiosk QR URL using user's site_id
  const siteId = user?.site_id;
  const kioskUrl = siteId ? `${FRONTEND_URL}/loan-car-kiosk/${siteId}` : null;

  if (!siteId) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <Card className="p-8 text-center max-w-md">
          <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-900 mb-2">No Workshop Assigned</h2>
          <p className="text-slate-600 mb-4">
            You need to be assigned to a workshop to generate a loan car QR code.
            Please contact your administrator.
          </p>
          <Button onClick={() => navigate(-1)}>Go Back</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header - Hidden during print */}
      <div className="print:hidden bg-white border-b p-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <Button onClick={handlePrint} className="bg-blue-600 hover:bg-blue-700">
            <Printer className="h-4 w-4 mr-2" />
            Print QR Code
          </Button>
        </div>
      </div>

      {/* Info Banner - Hidden during print */}
      <div className="print:hidden bg-green-50 border-b border-green-200 p-4">
        <div className="max-w-4xl mx-auto">
          <p className="text-green-800 text-sm">
            <strong>Permanent QR Code:</strong> This QR code never expires. Each customer scan creates a new loan car record. 
            Print it and place it at your desk for customers to scan when collecting a loan car.
          </p>
        </div>
      </div>

      {/* Printable Content */}
      <div className="p-8 print:p-0" ref={printRef}>
        <div className="max-w-[210mm] mx-auto bg-white print:shadow-none shadow-lg">
          {/* A4 Page */}
          <div className="p-12 print:p-8 min-h-[297mm] flex flex-col">
            {/* Header */}
            <div className="text-center mb-8 print:mb-6">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-600 rounded-2xl mb-4 print:w-16 print:h-16">
                <Car className="h-10 w-10 text-white print:h-8 print:w-8" />
              </div>
              <h1 className="text-4xl font-bold text-slate-900 print:text-3xl">
                Loan Car Check-In
              </h1>
              <p className="text-xl text-slate-600 mt-2 print:text-lg">
                Scan to fill in your details & collect your loan car
              </p>
            </div>

            {/* QR Code Section */}
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="bg-white p-8 rounded-3xl border-4 border-blue-600 inline-block shadow-lg print:shadow-none print:p-6">
                  <QRCodeSVG 
                    value={kioskUrl}
                    size={280}
                    level="H"
                    includeMargin={false}
                    className="print:w-[200px] print:h-[200px]"
                  />
                </div>
                
                <div className="mt-6 flex items-center justify-center gap-3 text-slate-500">
                  <Smartphone className="h-6 w-6" />
                  <span className="text-lg">Scan with your phone camera</span>
                </div>
              </div>
            </div>

            {/* Instructions */}
            <div className="mt-8 print:mt-4">
              <div className="bg-blue-50 rounded-xl p-6 print:p-4 border border-blue-200">
                <h2 className="font-bold text-blue-800 text-lg mb-4 print:text-base print:mb-2">
                  How it works:
                </h2>
                <div className="grid grid-cols-3 gap-4 print:gap-2 text-center">
                  <div className="space-y-2">
                    <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto text-xl font-bold print:w-10 print:h-10 print:text-lg">
                      1
                    </div>
                    <p className="text-slate-700 text-sm print:text-xs">
                      Scan the QR code with your phone
                    </p>
                  </div>
                  <div className="space-y-2">
                    <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto text-xl font-bold print:w-10 print:h-10 print:text-lg">
                      2
                    </div>
                    <p className="text-slate-700 text-sm print:text-xs">
                      Fill in your details, sign & accept terms
                    </p>
                  </div>
                  <div className="space-y-2">
                    <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto text-xl font-bold print:w-10 print:h-10 print:text-lg">
                      3
                    </div>
                    <p className="text-slate-700 text-sm print:text-xs">
                      Submit & collect your loan car
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-8 text-center text-slate-400 text-sm print:mt-4 print:text-xs">
              <p>Powered by AutoShopFlow</p>
              <p className="mt-1 font-mono text-xs print:hidden">{kioskUrl}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 0;
          }
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .print\\:hidden {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
}
